import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

interface ThemeContextType {
    theme: string;
    setTheme: (value: string) => void;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

interface ThemeProviderProps {
    children: ReactNode;
}

const getPreferredTheme = () => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
    }
    return 'light';
};

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
    const storedTheme = localStorage.getItem('theme') || getPreferredTheme();
    const [theme, setTheme] = useState(storedTheme === 'system' ? getPreferredTheme() : storedTheme);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme === 'system' ? getPreferredTheme() : theme);
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = () => {
            setTheme(getPreferredTheme());
            localStorage.setItem('theme', 'system');
        };
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);

    const toggleTheme = () => {
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        setTheme(newTheme);
        localStorage.setItem('theme', newTheme);
    };

    return <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>{children}</ThemeContext.Provider>;
};
